/**
 * Location service — fetches Ethiopian regions, zones, woredas, and cities
 * from Firestore with localStorage caching via cacheService.
 */

import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase/config";
import { cachedFetch } from "./cacheService";

const fetchCollection = async (col) => {
  return cachedFetch(col, async () => {
    try {
      // Try with orderBy first (requires index)
      const q = query(collection(db, col), orderBy("name"));
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.debug(`[locationService] fetched ${col}:`, docs.length);
      return docs;
    } catch (err) {
      console.warn(
        `[locationService] orderBy failed for ${col}, using client sort:`,
        err.message,
      );
      // Fallback: fetch all and sort client-side (no index needed)
      const snap = await getDocs(collection(db, col));
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      return docs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
  });
};

export const getRegions = () => fetchCollection("et_regions");
export const getZones = () => fetchCollection("et_zones");
export const getWoredas = () => fetchCollection("et_woredas");
export const getCities = () => fetchCollection("et_cities");

/**
 * Loads all location data at once and returns a structured lookup.
 * Useful for resolving IDs to names in display contexts.
 */
export const loadAllLocations = async () => {
  const [regions, zones, woredas, cities] = await Promise.all([
    getRegions(),
    getZones(),
    getWoredas(),
    getCities(),
  ]);

  const regionMap = Object.fromEntries(regions.map((r) => [r.id, r]));
  const zoneMap = Object.fromEntries(zones.map((z) => [z.id, z]));
  const woredaMap = Object.fromEntries(woredas.map((w) => [w.id, w]));
  const cityMap = Object.fromEntries(cities.map((c) => [c.id, c]));

  return {
    regions,
    zones,
    woredas,
    cities,
    regionMap,
    zoneMap,
    woredaMap,
    cityMap,
  };
};

/**
 * Resolve a city ID to its full location label: "CityName, Woreda, Zone, Region"
 */
export const resolveCityLabel = (
  cityId,
  { cityMap, woredaMap, zoneMap, regionMap },
) => {
  const city = cityMap?.[cityId];
  if (!city) return cityId;
  const woreda = woredaMap?.[city.woredaId];
  const zone = woreda ? zoneMap?.[woreda.zoneId] : null;
  const region = zone ? regionMap?.[zone.regionId] : null;
  return [city.name, woreda?.name, zone?.name, region?.name]
    .filter(Boolean)
    .join(", ");
};
